import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Box } from "@mui/material";
import { AssetClassCell } from "components/DealGroupedTable/AssetClassCell";
import { CharacteristicCell } from "components/DealGroupedTable/CharacteristicCell";
import {
  DEAL_NAME_COLUMN_KEY,
  ResolvedColumn,
  resolveSupportedColumns
} from "components/DealGroupedTable/dealGroupedTable.config";
import { cellStr, ColumnHeader, DealNameCell, DealRowData } from "components/DealGroupedTable/DealTableCells";
import { useActionHook } from "hooks";
import { MaterialReactTable, MRT_ColumnDef, MRT_SortingState, useMaterialReactTable } from "material-react-table";
import { useDealActions, useDealQueriesSelect } from "store";
import { DealAssetSimple, DealBarCharacteristic, DealsTableCell } from "store/types";

import { useGroupingView } from "../GroupingViewContext";

/** Row keys of the Asset / Sub Asset columns (a sub-asset depends on its asset). */
const ASSET_CLASS_KEY = "asset_class";
const SUB_ASSET_CLASS_KEY = "sub_asset_class";

/** Stable self-id for a row: deal id for leaves, group title (index fallback) for groups. */
const rowSelfId = (row: DealRowData, index: number): string =>
  row.id != null ? String(row.id) : cellStr(row["group_title"]) || `g${index}`;

/** All expandable (group) row ids in the tree — matches the table's getRowId scheme. */
const collectGroupKeys = (rows: DealRowData[], prefix = ""): string[] => {
  const keys: string[] = [];
  rows.forEach((row, index) => {
    const id = prefix + rowSelfId(row, index);
    if (row.children?.length) {
      keys.push(id);
      keys.push(...collectGroupKeys(row.children, `${id}/`));
    }
  });
  return keys;
};

export const DealListTable: React.FC = () => {
  const queries = useDealQueriesSelect();
  const { getDealsTableActions, getDealsTableColumnsActions, getAssetClassesAction } = useDealActions();
  const { isCollapsed, toggleGroup, registerGroupKeys } = useGroupingView();

  const { action, data, loading, setData } = useActionHook(getDealsTableActions);
  const { action: getDealsTableColumns } = useActionHook(getDealsTableColumnsActions);
  const { data: assetClasses, action: loadAssetClasses } = useActionHook(getAssetClassesAction);

  useEffect(() => {
    loadAssetClasses();
  }, []);

  const [allColumnDefs, setAllColumnDefs] = useState<{ key: string; title: string }[] | null>(null);

  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const getColumns = async () => {
    const columns = await getDealsTableColumns();
    setAllColumnDefs(columns);
  };

  useEffect(() => {
    getColumns();
  }, []);

  const tableColumns = queries.tableColumns;
  const valueKeys = tableColumns.filter((key) => key !== DEAL_NAME_COLUMN_KEY);
  const visibleColumns = useMemo<ResolvedColumn[]>(() => {
    if (!allColumnDefs) return [];
    const selected = new Set(valueKeys);
    return resolveSupportedColumns(allColumnDefs).filter((column) => selected.has(column.key));
  }, [tableColumns, allColumnDefs]);

  const columnOrder = useMemo(
    () => [DEAL_NAME_COLUMN_KEY, ...visibleColumns.map((column) => column.key)],
    [visibleColumns]
  );

  const deals_group_by = queries.dealListGroupBy;
  const groupsKey = deals_group_by.join(",");
  const columnsKey = columnOrder.join(",");

  useEffect(() => {
    if (!allColumnDefs) return; // wait until the supported columns are resolved
    action({ ...queries, columns: columnOrder, deals_group_by, view: "table" });
  }, [columnsKey, groupsKey, queries.deal_title, queries.filters, queries.order_by]);

  const groupKeys = useMemo(() => collectGroupKeys(data?.rows ?? []), [data]);

  useEffect(() => registerGroupKeys(groupKeys), [groupKeys, registerGroupKeys]);
  const expanded = useMemo<Record<string, boolean>>(
    () => Object.fromEntries(groupKeys.map((key) => [key, !isCollapsed(key)])),
    [groupKeys, isCollapsed]
  );

  const patchCell = useCallback(
    (rowId: number, columnKey: string, updated: DealsTableCell) => {
      setData((prev) => {
        if (!prev) return prev;
        const patchRows = (rows: DealRowData[]): DealRowData[] =>
          rows.map((row): DealRowData => {
            if (row.id === rowId) return { ...row, [columnKey]: updated } as DealRowData;
            if (row.children) return { ...row, children: patchRows(row.children) } as DealRowData;
            return row;
          });
        return { ...prev, rows: patchRows(prev.rows) };
      });
    },
    [setData]
  );

  const columns = useMemo<MRT_ColumnDef<DealRowData>[]>(() => {
    const labelKeys = columnOrder;
    const byLabel = new Map(visibleColumns.map((column) => [column.title, column]));
    const assetClass = byLabel.get("Asset Class");
    const subAssetClass = byLabel.get("Sub Asset Class");
    const geography = byLabel.get("Geography");
    const currency = byLabel.get("Currency");
    const vintage = byLabel.get("Vintage");

    return [
      {
        id: DEAL_NAME_COLUMN_KEY,
        header: "Deal Name",
        Header: () => <ColumnHeader title="Deal Name" />,
        accessorFn: (row) => cellStr(row["group_title"]) ?? cellStr(row[DEAL_NAME_COLUMN_KEY]),
        grow: true,
        size: 320,
        minSize: 220,
        Cell: ({ row }) => <DealNameCell row={row} labelKeys={labelKeys} onToggle={toggleGroup} />
      },
      ...(assetClass
        ? [
            {
              id: assetClass.key,
              header: "Asset Class",
              Header: () => <ColumnHeader title="Asset Class" />,
              accessorFn: (row) => cellStr(row[assetClass.key]),
              grow: true,
              size: 170,
              minSize: 120,
              Cell: ({ row }) =>
                row.getCanExpand() ? null : (
                  <AssetClassCell
                    mode="asset"
                    value={row.original[assetClass.key] as DealAssetSimple | null}
                    dealId={row.original.id}
                    assetClasses={assetClasses ?? []}
                    onSuccess={(updated) => {
                      patchCell(row.original.id, assetClass.key, updated);
                      patchCell(row.original.id, SUB_ASSET_CLASS_KEY, null);
                    }}
                  />
                )
            } as MRT_ColumnDef<DealRowData>
          ]
        : []),
      ...(subAssetClass
        ? [
            {
              id: subAssetClass.key,
              header: "Sub Asset Class",
              Header: () => <ColumnHeader title="Sub Asset Class" />,
              accessorFn: (row) => cellStr(row[subAssetClass.key]),
              grow: true,
              size: 170,
              minSize: 120,
              Cell: ({ row }) =>
                row.getCanExpand() ? null : (
                  <AssetClassCell
                    mode="subAsset"
                    value={row.original[subAssetClass.key] as DealAssetSimple | null}
                    dealId={row.original.id}
                    parentAsset={row.original[ASSET_CLASS_KEY] as DealAssetSimple | null}
                    assetClasses={assetClasses ?? []}
                    onSuccess={(updated) => patchCell(row.original.id, subAssetClass.key, updated)}
                  />
                )
            } as MRT_ColumnDef<DealRowData>
          ]
        : []),
      ...(geography
        ? [
            {
              id: geography.key,
              header: "Geography",
              Header: () => <ColumnHeader title="Geography" />,
              accessorFn: (row) => cellStr(row[geography.key]),
              grow: true,
              size: 170,
              minSize: 120,
              Cell: ({ row }) =>
                row.getCanExpand() ? null : (
                  <CharacteristicCell
                    value={row.original[geography.key] as DealBarCharacteristic | null}
                    dealId={row.original.id}
                    variant="link"
                    onSuccess={(updated) => patchCell(row.original.id, geography.key, updated)}
                  />
                )
            } as MRT_ColumnDef<DealRowData>
          ]
        : []),
      ...(currency
        ? [
            {
              id: currency.key,
              header: "Currency",
              Header: () => <ColumnHeader title="Currency" />,
              accessorFn: (row) => cellStr(row[currency.key]),
              grow: true,
              size: 170,
              minSize: 120,
              Cell: ({ row }) =>
                row.getCanExpand() ? null : (
                  <CharacteristicCell
                    value={row.original[currency.key] as DealBarCharacteristic | null}
                    dealId={row.original.id}
                    variant="link"
                    onSuccess={(updated) => patchCell(row.original.id, currency.key, updated)}
                  />
                )
            } as MRT_ColumnDef<DealRowData>
          ]
        : []),
      ...(vintage
        ? [
            {
              id: vintage.key,
              header: "Vintage",
              Header: () => <ColumnHeader title="Vintage" />,
              accessorFn: (row) => cellStr(row[vintage.key]),
              grow: true,
              size: 170,
              minSize: 120,
              Cell: ({ row }) =>
                row.getCanExpand() ? null : (
                  <CharacteristicCell
                    value={row.original[vintage.key] as DealBarCharacteristic | null}
                    dealId={row.original.id}
                    variant="link"
                    onSuccess={(updated) => patchCell(row.original.id, vintage.key, updated)}
                  />
                )
            } as MRT_ColumnDef<DealRowData>
          ]
        : [])
    ];
  }, [visibleColumns, columnOrder, patchCell, toggleGroup, assetClasses]);

  const table = useMaterialReactTable<DealRowData>({
    columns,
    data: data?.rows ?? [],
    layoutMode: "grid",
    enableColumnResizing: true,
    columnResizeMode: "onEnd",
    getRowId: (row, index, parent) => `${parent ? `${parent.id}/` : ""}${rowSelfId(row, index)}`,

    // -------- Performance (10k+ rows) --------
    enableRowVirtualization: true,
    rowVirtualizerOptions,

    // -------- Tree / expansion --------
    enableExpanding: true,
    getSubRows: (row) => row.children,

    // -------- Sorting (client-side) --------
    enableSorting: true,
    enableMultiSort: false,
    onSortingChange: setSorting,

    // -------- Disabled chrome --------
    enablePagination: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableTableFooter: false,
    enableStickyHeader: true,

    // -------- State / styling --------
    state: { isLoading: loading || allColumnDefs === null, columnOrder, expanded, sorting },
    // Expansion is controlled via the grouping context; toggles happen through it.
    onExpandedChange: () => undefined,
    displayColumnDefOptions,
    muiTablePaperProps: tablePaperProps,
    muiTableProps: tableProps,
    muiTableContainerProps: tableContainerProps,
    muiTableHeadCellProps: tableHeadCellProps,
    muiTableBodyCellProps: tableBodyCellProps,
    muiTableBodyRowProps: tableBodyRowProps
  });

  return (
    <Box sx={{ width: "100%", height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
      <MaterialReactTable table={table} />
    </Box>
  );
};

// ------------------------------ Styling ------------------------------------

const tablePaperProps = {
  sx: {
    borderRadius: "8px",
    boxShadow: "0 1px 2px 0 #1212171a",
    border: "1px solid #dbe8f5",
    height: "100%",
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column"
  }
} as const;

const tableProps = { sx: { width: "100%" } } as const;
const tableContainerProps = { sx: { width: "100%", flex: "1 1 auto", minHeight: 0, maxHeight: "100%" } } as const;

const tableHeadCellProps = {
  sx: {
    bgcolor: "#f6f8fc",
    color: "#071f35",
    fontWeight: 500,
    fontSize: "14px",
    borderBottom: "1px solid #dbe8f5",
    padding: "8px 20px",
    "& .Mui-TableHeadCell-ResizeHandle-Wrapper": { display: "none" }
  }
} as const;

const tableBodyCellProps = {
  sx: {
    bgcolor: "#fff",
    borderBottom: "1px solid #dbe8f5",
    padding: "0 20px",
    height: 48,
    fontSize: "15px",
    color: "#071f35"
  }
} as const;

const tableBodyRowProps = { sx: { "&:hover td": { backgroundColor: "#ecf1f8" } } } as const;

const rowVirtualizerOptions = { estimateSize: () => 48, overscan: 8 } as const;

const displayColumnDefOptions = {
  "mrt-row-expand": {
    size: 0,
    minSize: 0,
    grow: false,
    Header: () => null,
    Cell: () => null,
    muiTableHeadCellProps: { sx: { display: "none" } },
    muiTableBodyCellProps: { sx: { display: "none" } }
  }
} as const;
